import { useCallback, useEffect, useState } from "react";

import { ClientUser, IAddress } from "common/types";
import { pluralized, titleCased, toHumanizedList } from "common/utils/strings";
import {
  CartDeliveryUpdate,
  ControlledSubstanceLevel,
  DeliveryGroupKind,
  DeliveryOptionKind,
  ClientCartDetail,
  IDeliveryGroup,
  IDeliveryOption,
  isPickupDeliveryGroup,
  isPickupDeliveryOption,
  isShippingDeliveryGroup,
  isShippingDeliveryOption,
} from "common/types/carts";
import { toDollars } from "common/utils/formatting";
import { useFormState } from "common/hooks";
import { dateParts, toAge, toDateFromParts } from "common/utils/dates";
import { MONTH_SHORT_NAMES } from "common/constants/dates";
import Spinner from "common/components/Spinner";

import {
  useCartUpdateMutation,
  useControlledSubstancesSummaryMutation,
  useUpdateCartDeliveryMutation,
} from "client/carts/hooks";
import { CheckoutStepProps } from "client/checkout/types";
import DeliveryGroupMethodSelectPanel from "client/carts/components/shared/DeliveryGroupMethodSelectPanel";
import CartItemSummary from "client/carts/components/shared/CartItemSummary";
import SelectButton from "client/common/components/buttons/SelectButton";
import AddressSelectPanel from "client/accounts/components/AddressSelectPanel";
import { Clinic } from "client/clinics/types";
import SelectPanel from "client/common/components/inputs/SelectPanel";
import TextButton from "client/common/components/buttons/TextButton";
import Banner from "client/common/components/content/Banner";
import {
  useCreateAddressDrawer,
  useAddressUpdateDrawer,
  useExistingAddressConfirmationDialog,
  useControlledSubstancesInfoDrawer,
} from "client/accounts/stackables";
import TextInput from "client/common/components/inputs/TextInput";
import Select from "client/common/components/inputs/Select";
import StateSelect from "client/common/components/inputs/StateSelect";
import { useUpdateProfileMutation } from "client/accounts/hooks";
import Alert from "client/common/components/content/Alert";
import PrivacyDisclaimerTooltip from "client/common/components/content/PrivacyDisclaimerTooltip";
import { useFulfillerInfoDrawer } from "client/common/partners/stackables";
import { ControlledSubstancesSummary } from "client/carts/types";
import { sendAddShippingInfo, useSendPostHogUpdateShippingMethod } from "client/analytics/utils";

import StepLayout from "../CheckoutStepLayout";

import styles from "../../styles/DeliveryMethodCheckoutStep.module.less";

const DeliveryMethodShippingGroupDescription = ({ group }: { group: IDeliveryGroup }) => {
  const { open: openFulfillerInfoDrawer } = useFulfillerInfoDrawer();

  return (
    <>
      {group.description}
      <span>
        {" "}
        <TextButton hasUnderline={true} onClick={() => openFulfillerInfoDrawer()} icon="right">
          Learn more
        </TextButton>
      </span>
    </>
  );
};

type DeliveryMethodShippingGroupProps = {
  cart: ClientCartDetail;
  group: IDeliveryGroup;
  isOnlyGroup: boolean;
  heading: string;
};

const DeliveryMethodShippingGroup = (props: DeliveryMethodShippingGroupProps) => {
  const { cart, group, isOnlyGroup, heading } = props;
  const { mutate: updateCartDelivery, error } = useUpdateCartDeliveryMutation(cart.id);
  const sendPostHogUpdateShippingMethod = useSendPostHogUpdateShippingMethod();

  const selectDeliveryOption = (deliveryOption: IDeliveryOption) => {
    const cartItemUpdates: CartDeliveryUpdate[] = group.cart_items.map((ci) => {
      return deliveryOption.kind === DeliveryOptionKind.Pickup
        ? { cart_item_id: ci.id, is_pickup: true }
        : { cart_item_id: ci.id, shipping_method_id: deliveryOption.key };
    });
    const newDeliveryGroups = cart.delivery_groups.map((deliveryGroup) => {
      if (
        deliveryGroup.key === group.key &&
        isShippingDeliveryGroup(deliveryGroup) &&
        isShippingDeliveryOption(deliveryOption)
      ) {
        return { ...deliveryGroup, selected_delivery_option: deliveryOption };
      }
      if (
        deliveryGroup.key === group.key &&
        isPickupDeliveryGroup(deliveryGroup) &&
        isPickupDeliveryOption(deliveryOption)
      ) {
        return { ...deliveryGroup, selected_delivery_option: deliveryOption };
      }
      return deliveryGroup;
    });

    updateCartDelivery({
      body: cartItemUpdates,
      optimisticData: { newDeliveryGroups },
    });

    if (isShippingDeliveryGroup(group) && isShippingDeliveryOption(deliveryOption)) {
      sendPostHogUpdateShippingMethod({
        cart_id: cart.id,
        client_id: cart.client_id,
        clinic_id: cart.clinic.id,
        shipping_address_id: cart.shipping_address?.id ?? null,
        vendor_slug: group.fulfiller?.slug ?? null,
        shipping_method_id: deliveryOption.key,
        shipping_method_title: deliveryOption.title,
        shipping_cost: deliveryOption.cost,
        cart_item_ids: group.cart_items.map((cartItem) => cartItem.id),
      });
    }
  };

  return (
    <StepLayout.Item>
      {!isOnlyGroup && (
        <>
          <StepLayout.Header>
            <StepLayout.Heading>{heading}</StepLayout.Heading>
            <StepLayout.Subheading>
              <DeliveryMethodShippingGroupDescription group={group} />
            </StepLayout.Subheading>
          </StepLayout.Header>
          <StepLayout.Item.Info>
            <StepLayout.Section>
              {group.cart_items.map((cartItem) => (
                <CartItemSummary key={cartItem.id} cartItem={cartItem} />
              ))}
            </StepLayout.Section>
          </StepLayout.Item.Info>
        </>
      )}
      <StepLayout.Item.Content>
        <StepLayout.Section>
          <StepLayout.Header>
            <StepLayout.Heading>
              Select shipping speed
              {cart.shipping_address
                ? ` to ${titleCased(cart.shipping_address.city)}, ${cart.shipping_address.state}`
                : ""}
            </StepLayout.Heading>
          </StepLayout.Header>
          {error && <StepLayout.FetchErrorAlert error={error} />}
          {group.messages.map((message) => (
            <Banner variant="primary" key={message}>
              <Banner.Text align="left" label={message} />
            </Banner>
          ))}
          <DeliveryGroupMethodSelectPanel
            options={group.delivery_options}
            value={group.selected_delivery_option}
            onClick={selectDeliveryOption}
          />
        </StepLayout.Section>
      </StepLayout.Item.Content>
    </StepLayout.Item>
  );
};

type DeliveryMethodPickupGroupProps = {
  cart: ClientCartDetail;
  clinic: Clinic;
  group: IDeliveryGroup;
};

const DeliveryMethodPickupGroup = ({ cart, clinic, group }: DeliveryMethodPickupGroupProps) => {
  const { mutate: updateCart, isLoading, error } = useCartUpdateMutation(cart.id);

  const selectIsUrgent = (isUrgent: boolean) => {
    const updates = { is_urgent: isUrgent };

    updateCart({
      body: updates,
      cartUpdate: updates,
    });
  };

  const options = [
    {
      label: "Yes, this is an urgent order",
      description: `There is ${
        clinic.urgency_fee ? `a ${toDollars(clinic.urgency_fee)}` : "no"
      } supplemental urgency fee if this order is needed on the same day.`,
      isSelected: cart.is_urgent,
      value: true,
    },
    {
      label: "Not an urgent order",
      description: "This order will be ready in the next 24-48 hours",
      isSelected: !cart.is_urgent,
      value: false,
    },
  ];

  return (
    <StepLayout.Item>
      <StepLayout.Item.Info>
        <StepLayout.Section>
          <StepLayout.Section>
            {group.cart_items.map((cartItem) => (
              <CartItemSummary key={cartItem.id} cartItem={cartItem} />
            ))}
          </StepLayout.Section>
        </StepLayout.Section>
      </StepLayout.Item.Info>
      <StepLayout.Item.Content>
        <StepLayout.Section>
          <StepLayout.Header>
            <StepLayout.Heading>Is this an urgent order?</StepLayout.Heading>
          </StepLayout.Header>
          {error && <StepLayout.FetchErrorAlert error={error} />}
          <StepLayout.Section>
            <SelectPanel
              getKey={(option) => `${option?.isSelected}`}
              value={options.find((option) => option.isSelected)}
              getState={(option) => ({
                isLoading: isLoading && option.value === cart.is_urgent,
              })}
              options={options}
              onChange={(option) => selectIsUrgent(option.value)}
              renderOption={(option, values) => (
                <SelectButton.DefinitionContent align="start" {...option} {...values} />
              )}
            />
          </StepLayout.Section>
        </StepLayout.Section>
      </StepLayout.Item.Content>
    </StepLayout.Item>
  );
};

type DeliveryMethodCheckoutStepProps = CheckoutStepProps;

const getInitialControlledSubsValues = (clientUser: ClientUser) => {
  const { day, month, year } = clientUser.date_of_birth
    ? dateParts(clientUser.date_of_birth)
    : { day: "", month: "", year: "" };
  return {
    drivers_license_number: clientUser.drivers_license_number ?? "",
    drivers_license_state: clientUser.drivers_license_state ?? "",
    birth_day: `${day}`,
    birth_month: `${month}`,
    birth_year: `${year}`,
  };
};

type ControlledSubsFormValues = {
  drivers_license_number: string;
  drivers_license_state: string;
  birth_month: string;
  birth_day: string;
  birth_year: string;
};

const DeliveryMethodCheckoutStep = ({
  cart,
  clinic,
  client,
  onPrevious,
  onNext,
  isValid,
  isLoading,
}: DeliveryMethodCheckoutStepProps) => {
  const [controlledSubsSummary, setControlledSubsSummary] = useState<
    ControlledSubstancesSummary | undefined
  >(undefined);
  const {
    mutate: getControlledSubsInfo,
    isLoading: loadingControlledSubsInfo,
    error: getControlledSubsInfoError,
  } = useControlledSubstancesSummaryMutation({
    onSuccess: setControlledSubsSummary,
  });
  const {
    mutate: updateCart,
    error: updateError,
    isLoading: isUpdating,
  } = useCartUpdateMutation(cart.id);
  const {
    mutate: updateProfile,
    isLoading: isProfileUpdating,
    error: profileUpdateError,
  } = useUpdateProfileMutation({
    onSuccess: () => {
      getControlledSubsInfo({
        address_id: cart.shipping_address?.id,
        item_ids: cart.items.map((cartItem) => cartItem.item.id),
      });
    },
  });

  const { open: openExistingShippingAddressConfirmation } = useExistingAddressConfirmationDialog();
  const { open: openAddressUpdate } = useAddressUpdateDrawer();
  const { open: openAddressCreate } = useCreateAddressDrawer();
  const { open: openControlledSubstancesInfoDrawer } = useControlledSubstancesInfoDrawer();

  const shippingGroups = cart.delivery_groups.filter((g) => g.kind === DeliveryGroupKind.Shipped);
  const pickupGroups = cart.delivery_groups.filter((g) => g.kind === DeliveryGroupKind.Pickup);

  const [controlledSubsFields, updateControlledSubsFields] = useFormState(
    getInitialControlledSubsValues(client.primary_client_user),
  );

  useEffect(() => {
    if (!cart.shipping_address) {
      return;
    }

    getControlledSubsInfo({
      address_id: cart.shipping_address.id,
      item_ids: cart.items.map((item) => item.item.id),
    });
  }, [
    cart.items,
    cart.shipping_address,
    cart.shipping_address?.id,
    getControlledSubsInfo,
    client.primary_client_user,
  ]);

  const controlledSubsRequiredUIFields = controlledSubsSummary?.required_fields.flatMap((field) => {
    if (field === "date_of_birth") {
      return ["birth_month", "birth_day", "birth_year"];
    }
    return field;
  });

  const selectAddress = useCallback(
    (shipping_address: IAddress) => {
      updateCart({
        body: { shipping_address_id: shipping_address.id },
        cartUpdate: { shipping_address },
      });
    },
    [updateCart],
  );

  // Auto-select primary address if none are selected
  useEffect(() => {
    if (!cart.shipping_address && client.primary_address && !updateError && !isUpdating) {
      selectAddress(client.primary_address);
    }
    // don't use isUpdating as a dependency because it'll cause this to loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.shipping_address, client.primary_address, selectAddress, updateError]);

  const isValidDOB = controlledSubsSummary?.required_fields.includes("date_of_birth")
    ? client.primary_client_user.date_of_birth &&
      toAge(client.primary_client_user.date_of_birth) >= 18
    : true;

  // both cases being checked here in case the BE has no missing fields, but they are removed from the FE
  const canSubmitControlledSubs =
    (controlledSubsRequiredUIFields?.every(
      (field) => controlledSubsFields[field as keyof ControlledSubsFormValues] !== "",
    ) &&
      controlledSubsSummary?.missing_fields.length === 0 &&
      isValidDOB) ||
    // if we fail to load controlled subs, allow checkout
    !!getControlledSubsInfoError;

  const getTooltipText = () => {
    if (cart.warning_messages.length) {
      return "Please fix the issues with your cart items before proceeding.";
    }

    if (controlledSubsSummary?.is_controlled && (!canSubmitControlledSubs || !isValidDOB)) {
      return "Fill out Controlled Substances Information fields to proceed";
    }

    return !cart.is_all_pickup
      ? `Please select a shipping address and shipping ${pluralized(
          shippingGroups.length,
          "method",
        )} before proceeding.`
      : `Please make a selection for each item ${
          cart.has_subscription ? " and select a shipping address for future Autoships" : ""
        } before proceeding.`;
  };

  const handleNext = () => {
    sendAddShippingInfo(cart);
    onNext();
  };

  const onSubmit = () => {
    if (cart.shipping_address && cart.is_address_confirmation_required) {
      const shippingAddress = cart.shipping_address;
      openExistingShippingAddressConfirmation({
        address: shippingAddress,
        onConfirm: () => handleNext(),
        onDeny: () =>
          openAddressUpdate({
            address: shippingAddress,
            onSuccess: selectAddress,
          }),
        clinic,
      });
    } else {
      handleNext();
    }
  };

  const updateDateOfBirth = () => {
    updateProfile({
      date_of_birth: toDateFromParts({
        year: controlledSubsFields.birth_year,
        month: controlledSubsFields.birth_month,
        day: controlledSubsFields.birth_day,
      }),
    });
  };

  const renderControlledSubstancesInput = (field: string) => {
    switch (field) {
      case "drivers_license_number":
        return (
          <TextInput
            label="Driver's license ID"
            value={controlledSubsFields.drivers_license_number}
            onChange={updateControlledSubsFields("drivers_license_number")}
            onBlur={() => {
              updateProfile({
                drivers_license_number: controlledSubsFields.drivers_license_number,
              });
            }}
          />
        );
      case "drivers_license_state":
        return (
          <StateSelect
            label="Driver's license state"
            value={controlledSubsFields.drivers_license_state}
            onChange={updateControlledSubsFields("drivers_license_state")}
            onBlur={() => {
              updateProfile({ drivers_license_state: controlledSubsFields.drivers_license_state });
            }}
          />
        );
      case "date_of_birth":
        return (
          <div className={styles.dob}>
            <Select
              label="Your birth month"
              value={controlledSubsFields.birth_month}
              onChange={updateControlledSubsFields("birth_month")}
              className={styles.monthInput}
              onBlur={updateDateOfBirth}
            >
              {MONTH_SHORT_NAMES.map((monthName, index) => (
                <Select.Option value={`${index + 1}`} key={monthName}>
                  {monthName}
                </Select.Option>
              ))}
            </Select>
            <TextInput
              type="number"
              label="Your birth day"
              value={controlledSubsFields.birth_day}
              maxLength={2}
              onChange={updateControlledSubsFields("birth_day")}
              onBlur={updateDateOfBirth}
            />
            <TextInput
              type="number"
              label="Your birth year"
              value={controlledSubsFields.birth_year}
              maxLength={4}
              onChange={updateControlledSubsFields("birth_year")}
              onBlur={updateDateOfBirth}
            />
          </div>
        );
      default:
        // eslint-disable-next-line no-console
        console.log("Invalid required field for controlled substance.");
        return <></>;
    }
  };

  function renderControlledProductLinesText(controlledPls: number[]): string {
    const controlledPlNames: Set<string> = new Set(
      cart.items
        .map((ci) => ci.item.product.product_line)
        .filter((pl) => controlledPls.includes(pl.id))
        .map((pl) => pl.name),
    );

    const namesInSentence: string = toHumanizedList(controlledPlNames);

    return `${namesInSentence} ${
      controlledPlNames.size === 1 ? "is a controlled substance" : "are controlled substances"
    }`;
  }

  const hasOnlyOneShippingGroup = shippingGroups.length === 1;

  return (
    <StepLayout>
      {cart.is_shipping_address_required && (
        <>
          <StepLayout.Section>
            <StepLayout.Header>
              <StepLayout.Title>Shipping Address</StepLayout.Title>
              <StepLayout.Description>
                This shipping address will be used for future Autoship shipments.
              </StepLayout.Description>
            </StepLayout.Header>
            {updateError && (
              <StepLayout.FetchErrorAlert
                error={updateError}
                fieldErrors={[updateError.errors.shipping_address_id]}
              />
            )}
            <AddressSelectPanel
              options={client.addresses}
              value={cart.shipping_address}
              onClick={selectAddress}
              onAdd={() => openAddressCreate({ onSuccess: selectAddress })}
            />
          </StepLayout.Section>
          {!cart.is_all_pickup && (
            <StepLayout.Section>
              <StepLayout.Header>
                <StepLayout.Title>Shipping Speeds</StepLayout.Title>
                <StepLayout.Description>
                  {hasOnlyOneShippingGroup && (
                    <DeliveryMethodShippingGroupDescription group={shippingGroups[0]} />
                  )}
                </StepLayout.Description>
              </StepLayout.Header>
              {controlledSubsSummary?.is_controlled && (
                <StepLayout.Section>
                  <Alert variant="warning">
                    This order contains a controlled substance.{" "}
                    {controlledSubsSummary.controlled_level ===
                      ControlledSubstanceLevel.FederallyControlled &&
                      "An expedited shipping option must be selected. "}
                    Please allow additional processing time prior to shipping.
                  </Alert>
                </StepLayout.Section>
              )}
              {shippingGroups.map((deliveryGroup, index) => (
                <DeliveryMethodShippingGroup
                  key={deliveryGroup.key}
                  group={deliveryGroup}
                  cart={cart}
                  isOnlyGroup={hasOnlyOneShippingGroup}
                  heading={`Shipment ${index + 1} of ${shippingGroups.length}`}
                />
              ))}
            </StepLayout.Section>
          )}
        </>
      )}
      {controlledSubsSummary?.required_fields &&
        controlledSubsSummary.required_fields.length > 0 && (
          <StepLayout.Section>
            <StepLayout.Header>
              <StepLayout.Title>
                <div className={styles.controlledSubsTitle}>
                  <PrivacyDisclaimerTooltip>
                    Controlled Substances Information
                  </PrivacyDisclaimerTooltip>
                  {isProfileUpdating && <Spinner className={styles.spinner} />}
                </div>
              </StepLayout.Title>
              <StepLayout.Description>
                {renderControlledProductLinesText(
                  controlledSubsSummary.controlled_product_line_ids,
                )}{" "}
                in the state of {cart.shipping_address?.state}. Additional information is required
                for purchase.{" "}
                <TextButton
                  hasUnderline={true}
                  icon="right"
                  onClick={openControlledSubstancesInfoDrawer}
                >
                  Learn more
                </TextButton>
              </StepLayout.Description>
              <div className={styles.controlledSubsSection}>
                {profileUpdateError && <StepLayout.FetchErrorAlert error={profileUpdateError} />}
                {controlledSubsSummary?.is_controlled && !isValidDOB && (
                  <Alert variant="warning">
                    You must be 18 or older to order controlled substances. Please be sure to enter
                    your date of birth, not your pet&apos;s.
                  </Alert>
                )}
                {controlledSubsSummary?.required_fields.map((field) => (
                  <div key={field}>{renderControlledSubstancesInput(field)}</div>
                ))}
              </div>
            </StepLayout.Header>
          </StepLayout.Section>
        )}
      {cart.has_pickup && (
        <StepLayout.Section>
          <StepLayout.Header>
            <StepLayout.Title>Pickup Items</StepLayout.Title>
            <StepLayout.Description>
              These items will be picked up directly from the clinic
            </StepLayout.Description>
          </StepLayout.Header>
          {pickupGroups.map((deliveryGroup) => (
            <DeliveryMethodPickupGroup
              key={deliveryGroup.key}
              group={deliveryGroup}
              cart={cart}
              clinic={clinic}
            />
          ))}
        </StepLayout.Section>
      )}
      {controlledSubsSummary?.controlled_level === ControlledSubstanceLevel.FederallyControlled && (
        <StepLayout.Section>
          <Alert>
            <b>Shipping notice: </b> Please note controlled substances require an adult signature at
            time of delivery (must be 18+).
          </Alert>
        </StepLayout.Section>
      )}
      <StepLayout.Actions>
        <StepLayout.ActionButton.Previous onClick={onPrevious}>
          Return to Items
        </StepLayout.ActionButton.Previous>
        <StepLayout.ActionButton.Next
          isLoading={isLoading || isProfileUpdating || loadingControlledSubsInfo}
          onClick={onSubmit}
          disabled={!isValid || (controlledSubsSummary?.is_controlled && !canSubmitControlledSubs)}
          disabledHelp={getTooltipText()}
        >
          Continue to Payment
        </StepLayout.ActionButton.Next>
      </StepLayout.Actions>
    </StepLayout>
  );
};

export default DeliveryMethodCheckoutStep;
